import React, { useState, useEffect, useCallback, useRef } from "react";
import { Star, Zap, Compass, Sparkles, X } from "lucide-react";
import { IsaiahMascotCore } from "./IsaiahMascotCore";
import { getWorldDialogue, WORLD_ROLES } from "./IsaiahAnimationLibrary";
import "./isaiah-animations.css";

/**
 * IsaiahWorldReactions
 * Shows Isaiah's contextual reactions in world pages (Arcade, Adventure, Creator).
 * Appears at meaningful moments: game selection, wins, exploration triggers, build actions.
 * Reusable across all world pages.
 */

// World-specific reaction configurations with motionState for CSS animations
const WORLD_REACTION_CONFIG = {
  arcade: {
    welcome: { pose: "idle", motionState: "hype", duration: 3500, sparkle: true, dialogueKey: "welcome" },
    hype: { pose: "idle", motionState: "hype", duration: 2500, sparkle: true, dialogueKey: "hype" },
    gameStart: { pose: "idle", motionState: "excited", duration: 2000, sparkle: false, dialogueKey: "gameStart" },
    win: { pose: "idle", motionState: "excited", duration: 4000, sparkle: true, dialogueKey: "win" },
    loss: { pose: "idle", motionState: "idle", duration: 3000, sparkle: false, dialogueKey: "loss" },
    browse: { pose: "idle", motionState: "greeting", duration: 2500, sparkle: false, dialogueKey: "browse" },
  },
  adventure: {
    welcome: { pose: "idle", motionState: "exploring", duration: 3500, sparkle: true, dialogueKey: "welcome" },
    explore: { pose: "idle", motionState: "exploring", duration: 2500, sparkle: false, dialogueKey: "explore" },
    discovery: { pose: "idle", motionState: "excited", duration: 3500, sparkle: true, dialogueKey: "discovery" },
    storyStart: { pose: "idle", motionState: "looking", duration: 3000, sparkle: false, dialogueKey: "storyStart" },
    riddle: { pose: "idle", motionState: "pondering", duration: 3000, sparkle: false, dialogueKey: "riddle" },
    complete: { pose: "idle", motionState: "excited", duration: 4000, sparkle: true, dialogueKey: "complete" },
  },
  creator: {
    welcome: { pose: "idle", motionState: "crafting", duration: 3500, sparkle: true, dialogueKey: "welcome" },
    encourage: { pose: "idle", motionState: "crafting", duration: 2500, sparkle: false, dialogueKey: "encourage" },
    tip: { pose: "idle", motionState: "looking", duration: 3000, sparkle: false, dialogueKey: "tip" },
    save: { pose: "idle", motionState: "excited", duration: 3500, sparkle: true, dialogueKey: "save" },
    browse: { pose: "idle", motionState: "greeting", duration: 2500, sparkle: false, dialogueKey: "browse" },
  },
};

// World-specific default motion states for hero presence
const WORLD_HERO_MOTION = {
  arcade: "hype",
  adventure: "exploring",
  creator: "crafting",
};

// World-specific entrance animation classes
const WORLD_ENTRANCE_CLASS = {
  arcade: "isaiah-entrance-dash",
  adventure: "isaiah-entrance-slide-left",
  creator: "isaiah-entrance-pop",
};

/**
 * Hook: useIsaiahWorldReactions
 * Provides a trigger function for world-specific reactions.
 */
export const useIsaiahWorldReactions = (worldId) => {
  const [reaction, setReaction] = useState(null);
  const timerRef = useRef(null);

  const triggerReaction = useCallback((type, customMessage) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const worldConfig = WORLD_REACTION_CONFIG[worldId] || {};
    const config = worldConfig[type] || { pose: "wave", duration: 2500, sparkle: false, dialogueKey: "welcome" };
    const message = customMessage || getWorldDialogue(worldId, config.dialogueKey);

    setReaction({ type, ...config, message, worldId });

    timerRef.current = setTimeout(() => {
      setReaction(null);
    }, config.duration);
  }, [worldId]);

  const clearReaction = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setReaction(null);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return { reaction, triggerReaction, clearReaction };
};

/**
 * WorldSparkleBurst - World-themed sparkle effect
 */
const WorldSparkleBurst = ({ active, worldId }) => {
  if (!active) return null;

  const IconComponent = worldId === "arcade" ? Zap :
    worldId === "adventure" ? Compass :
    worldId === "creator" ? Sparkles : Star;

  const color = WORLD_ROLES[worldId]?.color || "#FBBF24";

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: 6 }, (_, i) => (
        <div
          key={i}
          className="absolute isaiah-energy-particle"
          style={{
            left: `${15 + Math.random() * 70}%`,
            top: `${5 + Math.random() * 60}%`,
            animationDelay: `${i * 0.12}s`,
          }}
        >
          <IconComponent size={10} style={{ color }} fill="currentColor" />
        </div>
      ))}
    </div>
  );
};

/**
 * IsaiahWorldReactionDisplay
 * Renders world-specific reaction overlay with themed styling.
 */
export const IsaiahWorldReactionDisplay = ({
  reaction,
  position = "bottom-right",
  size = "bust",
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (reaction) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [reaction]);

  if (!visible || !reaction) return null;

  const positionClasses = {
    "bottom-right": "bottom-2 right-2",
    "bottom-left": "bottom-2 left-2",
    "top-right": "top-2 right-2",
    "bottom-center": "bottom-2 left-1/2 -translate-x-1/2",
    "inline": "relative",
  };

  const isInline = position === "inline";
  const role = WORLD_ROLES[reaction.worldId];

  return (
    <div
      className={`${isInline ? "inline-flex items-end gap-2" : `absolute ${positionClasses[position]} z-30`} isaiah-entrance-pop`}
      data-testid="isaiah-world-reaction"
    >
      <div className="relative">
        <WorldSparkleBurst active={reaction.sparkle} worldId={reaction.worldId} />
        <IsaiahMascotCore
          pose={reaction.pose}
          size={size}
          glow={reaction.sparkle}
          motionState={reaction.motionState}
          testId="isaiah-world-reaction-character"
        />
      </div>
      {reaction.message && (
        <div className="isaiah-speech-enter" data-testid="isaiah-world-reaction-message">
          <div className="max-w-[170px] rounded-lg bg-black/60 px-2.5 py-1.5 shadow-xl" style={{ borderWidth: 1, borderColor: `${role?.color || '#fff'}30` }}>
            {role && <p className="text-[7px] font-bold uppercase tracking-wider mb-0.5" style={{ color: role.color }}>{role.title}</p>}
            <p className="text-white/90 text-[10px] leading-relaxed font-semibold">{reaction.message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * IsaiahWorldHero
 * A hero-section presence for world landing pages.
 * Shows Isaiah in his idle pose with world-themed glow and speech.
 */

export const IsaiahWorldHero = ({
  worldId,
  pose,
  message,
  size = "full-body",
}) => {
  const [currentMessage, setCurrentMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(`isaiah_hero_hidden_${worldId}`) === "1"; } catch { return false; }
  });
  const role = WORLD_ROLES[worldId];

  useEffect(() => {
    const t = setTimeout(() => setShowMessage(true), 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setCurrentMessage(message || getWorldDialogue(worldId, "welcome"));
  }, [worldId, message]);

  if (!role) return null;

  const hide = () => { try { localStorage.setItem(`isaiah_hero_hidden_${worldId}`, "1"); } catch {} ; setHidden(true); };
  const show = () => { try { localStorage.removeItem(`isaiah_hero_hidden_${worldId}`); } catch {} ; setHidden(false); };

  // Hidden: a small peek tab sticks out from the far-right edge to bring him back.
  if (hidden) {
    return (
      <button
        onClick={show}
        data-testid={`isaiah-${worldId}-hero-restore`}
        aria-label={`Show ${role.title} Isaiah`}
        className="fixed right-0 top-[150px] z-40 flex items-center justify-center isaiah-peek-wiggle"
        style={{ width: 30, height: 52, borderTopLeftRadius: 9999, borderBottomLeftRadius: 9999,
          border: `1px solid ${role.color}66`, borderRight: "none", background: `${role.color}D9`, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}
      >
        <span style={{ fontSize: 20 }}>🌟</span>
      </button>
    );
  }

  const glowClass = `isaiah-glow-${worldId}`;
  const entranceClass = WORLD_ENTRANCE_CLASS[worldId] || "isaiah-entrance-rise";
  const heroMotion = WORLD_HERO_MOTION[worldId] || "idle";

  return (
    <div className={`relative ${entranceClass}`} data-testid={`isaiah-${worldId}-hero`}>
      {/* Tap to tuck Isaiah out of the way; a tab on the right brings him back. */}
      <button
        onClick={hide}
        data-testid={`isaiah-${worldId}-hero-hide`}
        aria-label="Hide Isaiah"
        className="absolute -top-1 -left-1 z-20 w-7 h-7 rounded-full bg-black/85 border border-white/30 flex items-center justify-center hover:bg-black hover:scale-110 transition-all"
      >
        <X size={14} className="text-white/85" />
      </button>
      <div className="relative">
        <div
          className={`absolute pointer-events-none ${glowClass}`}
          style={{ top: "10%", left: "5%", right: "5%", bottom: "0", filter: "blur(24px)", zIndex: -1 }}
        />
        <IsaiahMascotCore
          pose={pose || "idle"}
          size={size}
          glow
          breathing
          danceable
          motionState={heroMotion}
          testId={`isaiah-${worldId}-hero-character`}
        />
      </div>
      {showMessage && currentMessage && (
        <div className="absolute right-full mr-2 top-6 sm:top-10 z-30 isaiah-speech-enter" data-testid={`isaiah-${worldId}-hero-speech`}>
          <div className="relative rounded-2xl bg-black/85 backdrop-blur-sm px-3 py-2 shadow-xl w-[140px] sm:w-[180px]" style={{ borderWidth: 1, borderColor: `${role.color}40` }}>
            <p className="text-[8px] font-bold uppercase tracking-wider mb-0.5" style={{ color: role.color }}>{role.title} Isaiah</p>
            <p className="text-white/90 text-[11px] leading-relaxed font-semibold">{currentMessage}</p>
            {/* Tail pointing right, toward Isaiah */}
            <span className="absolute top-5 -right-1.5 w-3 h-3 rotate-45 bg-black/85" style={{ borderRight: `1px solid ${role.color}40`, borderTop: `1px solid ${role.color}40` }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default IsaiahWorldReactionDisplay;
